import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect, Textarea } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Badge } from '@/components/ui/Badge';
import { LoadingState, ErrorState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import VoiceMicButton from '@/components/VoiceMicButton';
import VoiceFormFiller, { type VoiceField } from '@/components/VoiceFormFiller';
import { useAllergies, useCreateAllergy, useRetractAllergy } from './useAllergies';
import type { Allergy, AllergyCategory, AllergyCriticality } from './types';

const CATEGORIES: AllergyCategory[] = ['medication', 'food', 'environment', 'biologic'];
const CRITICALITIES: AllergyCriticality[] = ['high', 'low', 'unable-to-assess'];
const NO_KNOWN_ALLERGIES = 'No known allergies';

const VOICE_FIELDS: VoiceField[] = [
  { key: 'substance', label: 'Substance', type: 'text' },
  { key: 'category', label: 'Category', type: 'text', options: CATEGORIES },
  { key: 'criticality', label: 'Criticality', type: 'text', options: CRITICALITIES },
  { key: 'reaction', label: 'Reaction', type: 'text' },
];

function criticalityVariant(criticality: AllergyCriticality | null): 'danger' | 'warn' | 'neutral' {
  if (criticality === 'high') return 'danger';
  if (criticality === 'unable-to-assess') return 'warn';
  return 'neutral';
}

interface AllergyPanelProps {
  uhid: string;
  /**
   * Free-text `allergies` captured by the nurse in Pre-Consult triage
   * (stage_data.pre_consult.allergies) — there's no structured allergy table
   * write path at that stage, so this is prose, not a row. When supplied
   * (only ever from TriageAllergyChip's "Add to record…" dialog — see
   * triageAllergyReconciliation.ts), the add form opens already expanded and
   * pre-filled with this text, so the doctor doesn't have to retype it.
   */
  preConsultAllergiesText?: string;
}

export function AllergyPanel({ uhid, preConsultAllergiesText }: AllergyPanelProps) {
  // Seeded from the prop rather than a click — the only caller that ever
  // supplies preConsultAllergiesText is TriageAllergyChip's dedicated "Add to
  // record…" dialog (see triageAllergyReconciliation.ts), which mounts a
  // fresh AllergyPanel purely for that purpose. Opening straight into a
  // pre-filled form is one less click than the amber "From Pre-Consult
  // triage" nudge this replaced. The ordinary "Manage allergies" path
  // (PatientBanner's Why? popover) never passes this prop, so it still opens
  // closed/empty exactly as before.
  const [adding, setAdding] = useState(!!preConsultAllergiesText);
  const [substance, setSubstance] = useState(preConsultAllergiesText ?? '');
  const [category, setCategory] = useState<AllergyCategory | ''>('');
  const [criticality, setCriticality] = useState<AllergyCriticality | ''>('');
  const [reaction, setReaction] = useState('');
  const [formError, setFormError] = useState('');

  const query = useAllergies(uhid);
  const createMutation = useCreateAllergy(uhid);
  const retractMutation = useRetractAllergy(uhid);

  const resetForm = () => {
    setAdding(false); setSubstance(''); setCategory(''); setCriticality(''); setReaction(''); setFormError('');
  };

  const applyVoiceFields = (extracted: Record<string, string>) => {
    if (extracted.substance) setSubstance(extracted.substance);
    if (extracted.category && (CATEGORIES as string[]).includes(extracted.category)) setCategory(extracted.category as AllergyCategory);
    if (extracted.criticality && (CRITICALITIES as string[]).includes(extracted.criticality)) setCriticality(extracted.criticality as AllergyCriticality);
    if (extracted.reaction) setReaction(extracted.reaction);
  };

  const submit = async () => {
    const trimmed = substance.trim();
    if (!trimmed) { setFormError('A substance is required'); return; }
    setFormError('');
    try {
      await createMutation.mutateAsync({
        substance: trimmed,
        category: category || null,
        criticality: criticality || null,
        reaction: reaction.trim() || null,
      });
      resetForm();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : String(e));
    }
  };

  const affirmNoKnownAllergies = async () => {
    try {
      await createMutation.mutateAsync({ substance: NO_KNOWN_ALLERGIES });
    } catch { /* surfaced via createMutation.error below */ }
  };

  const allergies = query.data ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle><AlertTriangle size={15} /> Allergies</CardTitle>
        {!adding && (
          <div className="flex gap-2">
            {allergies.length === 0 && (
              <Button size="sm" variant="ghost" loading={createMutation.isPending} onClick={affirmNoKnownAllergies}>
                No known allergies
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => setAdding(true)}>+ Add allergy</Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {query.isLoading && <LoadingState label="Loading allergies…" />}
        {query.isError && (
          <ErrorState
            message={query.error instanceof ApiError ? query.error.message : 'Could not load allergies'}
            onRetry={() => query.refetch()}
          />
        )}

        {!query.isLoading && !query.isError && allergies.length === 0 && !adding && (
          <div className="text-[13px] text-ink-muted">Nothing recorded yet — ask the patient, then record an allergy or affirm none are known.</div>
        )}

        {!query.isLoading && allergies.length > 0 && (
          <div className="flex flex-col gap-2">
            {allergies.map((a: Allergy) => (
              <div key={a.id} className="flex items-start justify-between gap-2 rounded-[var(--radius)] border border-border px-3 py-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[13px] font-semibold">{a.substance}</span>
                    {a.criticality && <Badge variant={criticalityVariant(a.criticality)}>{a.criticality}</Badge>}
                    {a.category && <Badge variant="neutral">{a.category}</Badge>}
                  </div>
                  {a.reaction && <div className="mt-0.5 text-[12px] text-ink-muted">Reaction: {a.reaction}</div>}
                </div>
                {a.substance !== NO_KNOWN_ALLERGIES && (
                  <Button
                    size="sm" variant="ghost"
                    loading={retractMutation.isPending}
                    onClick={() => retractMutation.mutate(a.id)}
                  >
                    ✕
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        {retractMutation.isError && (
          <div className="mt-2 text-[11px] text-danger">
            {retractMutation.error instanceof ApiError ? retractMutation.error.message : 'Could not remove that entry'}
          </div>
        )}

        {adding && (
          <div className="mt-3 flex flex-col gap-2.5 rounded-[var(--radius)] border border-border p-3">
            <VoiceFormFiller fields={VOICE_FIELDS} onFields={applyVoiceFields} label="🎙 Fill this allergy by voice" />
            <FormField label="Substance" required labelExtra={<VoiceMicButton onText={t => setSubstance(v => (v ? v + ' ' : '') + t)} />}>
              <Input value={substance} onChange={e => setSubstance(e.target.value)} placeholder="e.g. Penicillin" autoFocus />
            </FormField>
            <div className="grid grid-cols-2 gap-2.5">
              <FormField label="Category">
                <NativeSelect value={category} onChange={e => setCategory(e.target.value as AllergyCategory | '')}>
                  <option value="">—</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </NativeSelect>
              </FormField>
              <FormField label="Criticality">
                <NativeSelect value={criticality} onChange={e => setCriticality(e.target.value as AllergyCriticality | '')}>
                  <option value="">—</option>
                  {CRITICALITIES.map(c => <option key={c} value={c}>{c}</option>)}
                </NativeSelect>
              </FormField>
            </div>
            <FormField label="Reaction" labelExtra={<VoiceMicButton onText={t => setReaction(v => (v ? v + ' ' : '') + t)} />}>
              <Textarea value={reaction} onChange={e => setReaction(e.target.value)} placeholder="e.g. Rash, swelling" />
            </FormField>
            {formError && <div className="text-[11px] text-danger">{formError}</div>}
            <div className="flex gap-2">
              <Button size="sm" loading={createMutation.isPending} onClick={submit}>Save allergy</Button>
              <Button size="sm" variant="ghost" onClick={resetForm}>Cancel</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
